import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { useNavigate } from 'react-router-dom';
import { getTrainingAnalytics, GetTrainingAnalyticsOutputType } from 'zite-endpoints-sdk';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { BarChart3, Download, AlertTriangle, Clock, Award, Users, TrendingUp } from 'lucide-react';
import { isManagerPlus } from '@/lib/roles';

type Analytics = GetTrainingAnalyticsOutputType;

export default function TrainingAnalytics() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    if (!isManagerPlus(user.role)) { navigate('/'); return; }
    getTrainingAnalytics({}).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [user, navigate]);

  const exportCsv = () => {
    if (!data) return;
    const lines = ['Training Analytics Export', '', 'Department,Total Staff,Completed,In Progress,Not Started'];
    data.byDepartment.forEach(d => lines.push(`${d.department},${d.total},${d.completed},${d.inProgress},${d.notStarted}`));
    lines.push('', 'Overdue Items', 'Staff,Item,Type,Deadline');
    data.overdue.forEach(o => lines.push(`"${o.staffName}","${o.item}","${o.type}","${o.deadline}"`));
    lines.push('', 'Expiring Certificates', 'Staff,Certificate,Expiry,Days Left');
    data.expiringCerts.forEach(c => lines.push(`"${c.staffName}","${c.certName}","${c.expiryDate}",${c.daysLeft}`));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'training-analytics.csv'; a.click();
  };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-32 w-full" />)}</div>;
  if (!data) return null;

  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-xl font-bold">Training Analytics</h1>
            <p className="text-sm text-muted-foreground">{data.totalStaff} active staff</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv}><Download className="w-4 h-4 mr-1.5" />Export CSV</Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold">{data.totalStaff}</p><p className="text-xs text-muted-foreground">Total Staff</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold text-red-500">{data.overdue.length}</p><p className="text-xs text-muted-foreground">Overdue</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold text-amber-500">{data.dueSoon.length}</p><p className="text-xs text-muted-foreground">Due Soon</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold text-primary">{data.expiringCerts.length}</p><p className="text-xs text-muted-foreground">Expiring Certs</p></CardContent></Card>
      </div>

      <Tabs defaultValue="department">
        <TabsList className="flex-wrap">
          <TabsTrigger value="department"><Users className="w-3.5 h-3.5 mr-1" />By Department</TabsTrigger>
          <TabsTrigger value="role">By Role</TabsTrigger>
          <TabsTrigger value="overdue"><AlertTriangle className="w-3.5 h-3.5 mr-1" />Overdue</TabsTrigger>
          <TabsTrigger value="certs"><Award className="w-3.5 h-3.5 mr-1" />Certs</TabsTrigger>
          <TabsTrigger value="starters"><TrendingUp className="w-3.5 h-3.5 mr-1" />New Starters</TabsTrigger>
        </TabsList>

        <TabsContent value="department" className="space-y-3 mt-4">
          {data.byDepartment.map(d => {
            const pct = d.total > 0 ? Math.round((d.completed / d.total) * 100) : 0;
            return (
              <Card key={d.department}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium">{d.department}</p>
                    <p className="text-xs text-muted-foreground">{d.completed}/{d.total} complete</p>
                  </div>
                  <Progress value={pct} className="h-2" />
                  <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                    <span>✅ {d.completed}</span><span>🔄 {d.inProgress}</span><span>⏳ {d.notStarted}</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="role" className="space-y-3 mt-4">
          {data.byRole.map(r => (
            <Card key={r.role}>
              <CardContent className="py-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{r.role}</p>
                  <p className="text-xs text-muted-foreground">{r.total} staff</p>
                </div>
                <div className="flex gap-2">
                  <Badge variant="default">{r.completed} done</Badge>
                  <Badge variant="secondary">{r.inProgress} in progress</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="overdue" className="space-y-3 mt-4">
          {data.overdue.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No overdue items 🎉</p> : data.overdue.map((o, i) => (
            <Card key={i}>
              <CardContent className="py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{o.staffName}</p>
                  <p className="text-xs text-muted-foreground">{o.item} · {o.type}</p>
                </div>
                <Badge variant="destructive">{o.deadline}</Badge>
              </CardContent>
            </Card>
          ))}
          {data.complianceGaps.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Compliance Gaps</CardTitle></CardHeader>
              <CardContent>
                {data.complianceGaps.map((g, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0">
                    <span className="text-sm">{g.staffName}</span>
                    <Badge variant="destructive" className="text-xs">{g.status}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="certs" className="space-y-3 mt-4">
          {data.expiringCerts.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No expiring certificates</p> : data.expiringCerts.map((c, i) => (
            <Card key={i}>
              <CardContent className="py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{c.staffName}</p>
                  <p className="text-xs text-muted-foreground">{c.certName}</p>
                </div>
                <Badge variant={c.daysLeft <= 0 ? 'destructive' : c.daysLeft <= 30 ? 'default' : 'secondary'}>
                  {c.daysLeft <= 0 ? 'Expired' : `${c.daysLeft}d left`}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="starters" className="space-y-3 mt-4">
          {data.newStarters.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No new starters in the last 90 days</p> : data.newStarters.map((s, i) => (
            <Card key={i}>
              <CardContent className="py-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium">{s.name}</p>
                  <Badge variant="secondary">{s.onboardingStatus}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">Started {s.startDate} · {s.modulesCompleted}/{s.modulesTotal} modules</p>
                <Progress value={s.modulesTotal > 0 ? (s.modulesCompleted / s.modulesTotal) * 100 : 0} className="h-1.5 mt-2" />
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
